import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { Window } from "@progress/kendo-react-dialogs";
import { Field, Form, FormElement } from "@progress/kendo-react-form";
import type { FieldRenderProps, FormRenderProps } from "@progress/kendo-react-form";
import { Error as KendoError } from "@progress/kendo-react-labels";

/* NEW: avatar in title */
import { Avatar } from "@progress/kendo-react-layout";
import { SvgIcon } from "@progress/kendo-react-common";
import { userIcon } from "@progress/kendo-svg-icons";

import "./styles.css";

import { AuthService } from "@infrastructure/services";
import { useInjection } from "@presentation/components/dependencyInjection";
import { useNotifications } from "@presentation/components/notifications";

import { ChangePassword } from "@application/useCases";
import { ChangeMyPassword } from "@domain/schemas/BI";

/* -------------------- Types & Guards (no `any`) -------------------- */
type ValueGetter = (name: string) => unknown;
type AxiosLike<T = unknown> = { status: number; data?: T };
type FetchLike = { status: number; ok: boolean };

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
function isAxiosLike<T = unknown>(r: unknown): r is AxiosLike<T> {
  return isObject(r) && "status" in r && typeof r.status === "number" && !("ok" in r);
}
function isFetchLike(r: unknown): r is FetchLike {
  return isObject(r) && "status" in r && typeof r.status === "number" && "ok" in r && typeof (r as Record<string, unknown>).ok === "boolean";
}
function isSuccessResponse(r: unknown): boolean {
  if (r == null) return true;
  if (isFetchLike(r)) return r.ok;
  if (isAxiosLike(r)) return r.status >= 200 && r.status < 300;
  return true;
}
function isChangeMyPassword(v: unknown): v is ChangeMyPassword {
  return isObject(v) && "currentPassword" in v && "newPassword" in v && "confirmNewPassword" in v;
}
type ApiError = {
  response?: { data?: { title?: string; detail?: string; errors?: Record<string, string[]> }; status?: number };
  message?: string;
};

/* -------------------- Validators -------------------- */
const required =
  (message: string) =>
    (value?: string): string | undefined =>
      value?.trim() ? undefined : message;

const passwordsMatch = (_: string | undefined, valueGetter: ValueGetter): string | undefined => {
  const newPwd = (valueGetter("newPassword") as string | undefined) ?? "";
  const confirm = (valueGetter("confirmNewPassword") as string | undefined) ?? "";
  return newPwd === confirm ? undefined : "Passwords must match";
};

const confirmRequiredAndMatch = (value: string | undefined, valueGetter: ValueGetter): string | undefined => required("Please confirm the new password")(value) || passwordsMatch(value, valueGetter);

/* -------------------- Component -------------------- */
type ProfileWindowProps = { onClose: () => void; username?: string };

const ProfileWindow: React.FC<ProfileWindowProps> = ({ onClose, username }) => {
  const { pushNotification } = useNotifications();
  const authService = useInjection(AuthService);
  const changePasswordUseCase = useInjection(ChangePassword);

  const derivedUsername = username ?? "";

  const [formData] = React.useState<ChangeMyPassword>({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const handlePasswordChangeSubmit = async (values: unknown): Promise<void> => {
    if (!isChangeMyPassword(values)) throw new Error("Invalid payload for ChangeMyPassword.");
    try {
      const res = await changePasswordUseCase.executeAsync(values);
      if (isSuccessResponse(res)) {
        pushNotification("Password updated successfully! Please sign in with the new password.", "success", 5000);
        await Promise.resolve(authService?.signOut?.());
        onClose();
        return;
      }
      let status: number | undefined;
      if (isFetchLike(res)) status = res.status;
      else if (isAxiosLike(res)) status = res.status;
      throw new Error(`Unexpected response${status ? ` (status ${status})` : ""} while changing password.`);
    } catch (err: unknown) {
      const error = err as ApiError;
      const responseData = error.response?.data;
      const title = responseData?.title || error.message || "Password change failed.";
      const detail = responseData?.detail;
      const validationErrors = responseData?.errors;
      const messages: string[] = [];

      if (validationErrors) {
        for (const key of Object.keys(validationErrors)) {
          const list = validationErrors[key];
          if (Array.isArray(list)) messages.push(...list);
        }
      }
      if (messages.length === 0) messages.push(detail || title);

      const formattedMessage = `Password change failed:\n\n${messages.map((m) => `• ${m}`).join("\n")}`;
      console.error(formattedMessage);
      pushNotification(formattedMessage, "error", 10000);
    }
  };

  const PasswordField: React.FC<FieldRenderProps> = (fieldRenderProps) => {
    const { validationMessage, visited, ...others } = fieldRenderProps;
    return (
      <div className="k-form-field">
        <Input {...others} style={{ width: "100%" }} />
        {visited && validationMessage && <KendoError>{validationMessage}</KendoError>}
      </div>
    );
  };

  /* ✨ Custom Window title with avatar + "My Profile" */
  const titleNode = (
    <div className="profile-window-title">
      <Avatar rounded="full" type="icon" className="user-avatar">
        <SvgIcon icon={userIcon} />
      </Avatar>
      <h3>My Profile</h3>
    </div>
  );

  return (
    <Window
      title={titleNode}
      onClose={onClose}
      className="profile-window"
      initialWidth={400} // starting width; CSS will clamp responsively
      resizable={false}
      draggable={true}
    >
      <div className="profile-content">
        <p className="profile-username-line">
          <h3 className="profile-section-title">Username: {derivedUsername}</h3>
          <h3 className="profile-section-title">Roles: {""}</h3>
        </p>

        <fieldset className={"k-form-fieldset"}>
          <legend className={"k-form-legend"} style={{ marginBottom: "-10px" }}>
            <h3 className="profile-section-title">Change my password</h3>
          </legend>
          <div className="k-form-field-wrap">
            <Form
              onSubmit={handlePasswordChangeSubmit}
              initialValues={formData}
              render={(formRenderProps: FormRenderProps) => (
                <FormElement className="profile-form">
                  <Field name="currentPassword" component={PasswordField} label="Current Password" type="password" validator={required("Current password is required")} />
                  <Field name="newPassword" component={PasswordField} label="New Password" type="password" validator={required("New password is required")} />
                  <Field name="confirmNewPassword" component={PasswordField} label="Repeat New Password" type="password" validator={confirmRequiredAndMatch} />

                  <div className="k-form-buttons">
                    <Button type="submit" themeColor="primary" disabled={!formRenderProps.valid} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base">
                      Change Password
                    </Button>
                  </div>
                </FormElement>
              )}
            />
          </div>
        </fieldset>
      </div>
    </Window>
  );
};

export { ProfileWindow };
